import React, { useEffect, useRef } from 'react';
import { resolveBrowserApiBaseUrl } from 'utils/apiBaseUrl';

interface SseNotificationsProps {
  id: string;
  setMessage: (message: string) => void;
  type: 'receipts' | 'report' | 'entity';
}

type SharedEventSource = {
  source: EventSource;
  listeners: Set<(data: string) => void>;
  refCount: number;
  closeTimer: ReturnType<typeof setTimeout> | null;
  reconnectTimer: ReturnType<typeof setTimeout> | null;
};

const sharedSources = new Map<string, SharedEventSource>();

const reopenSource = (url: string, shared: SharedEventSource) => {
  try {
    shared.source.close();
  } catch {
    // Ignore already-closed sockets.
  }

  const source = new EventSource(url, { withCredentials: true });
  const deliver = (data: string) => {
    shared.listeners.forEach((listener) => listener(data));
  };

  source.addEventListener('notification', (event) => {
    deliver(event.data);
  });
  // Some mobile browsers only surface unnamed SSE frames on `onmessage`.
  source.onmessage = (event) => {
    deliver(event.data);
  };
  source.onerror = () => {
    // Let the browser reconnect while CONNECTING. Only replace a CLOSED socket.
    if (source.readyState !== EventSource.CLOSED) return;
    if (shared.refCount <= 0 || shared.reconnectTimer) return;
    shared.reconnectTimer = setTimeout(() => {
      shared.reconnectTimer = null;
      if (shared.refCount <= 0) return;
      shared.source = reopenSource(url, shared);
    }, 1000);
  };

  return source;
};

function subscribeToEventSource(
  url: string,
  onMessage: (data: string) => void
): () => void {
  let shared = sharedSources.get(url);
  if (!shared) {
    shared = {
      source: null as unknown as EventSource,
      listeners: new Set<(data: string) => void>(),
      refCount: 0,
      closeTimer: null,
      reconnectTimer: null,
    };
    shared.source = reopenSource(url, shared);
    sharedSources.set(url, shared);
  }

  if (shared.closeTimer) {
    clearTimeout(shared.closeTimer);
    shared.closeTimer = null;
  }
  shared.listeners.add(onMessage);
  shared.refCount += 1;

  let wasBackgrounded = false;
  const reopenAfterBackground = () => {
    const current = sharedSources.get(url);
    if (!current || current.refCount <= 0) return;
    current.source = reopenSource(url, current);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'hidden') {
      wasBackgrounded = true;
      return;
    }
    if (!wasBackgrounded) return;
    wasBackgrounded = false;
    reopenAfterBackground();
  };

  const onPageShow = (event: Event) => {
    const persisted =
      typeof PageTransitionEvent !== 'undefined' &&
      event instanceof PageTransitionEvent &&
      event.persisted;
    if (!persisted && !wasBackgrounded) return;
    wasBackgrounded = false;
    reopenAfterBackground();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pageshow', onPageShow);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pageshow', onPageShow);
    shared.listeners.delete(onMessage);
    shared.refCount -= 1;
    if (shared.refCount > 0) return;
    // StrictMode remounts in the same turn; keep the socket so events are not
    // delivered to a connection that React already closed.
    shared.closeTimer = setTimeout(() => {
      if (shared.refCount > 0) return;
      if (shared.reconnectTimer) {
        clearTimeout(shared.reconnectTimer);
        shared.reconnectTimer = null;
      }
      shared.source.close();
      sharedSources.delete(url);
    }, 0);
  };
}

export const SseNotifications: React.FC<SseNotificationsProps> = ({
  id,
  setMessage,
  type,
}) => {
  const setMessageRef = useRef(setMessage);
  setMessageRef.current = setMessage;

  useEffect(() => {
    if (!id) return;

    const apiUrl = resolveBrowserApiBaseUrl(process.env.API_URL);
    const url = `${apiUrl}/sse/subscribe/${type}/${id}`;
    return subscribeToEventSource(url, (data) => {
      setMessageRef.current?.(data);
    });
  }, [id, type]);

  return null;
};

export default SseNotifications;
